import React, { useEffect, useCallback, useState } from "react";
import { Container, Row, Col, Card, CardBody, CardHeader, Input, Label, Button, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { socket } from "../../../common/socket";

// Redux actions
import { fetchSettingsData, updateSettingsAction, declareManualWinner } from "../../../slices/lt1/settings/thunk";
import { updateGameState, updateBidPredictions, setManualWinnerWindow, resetRound, setSelectedWinner, updateHistory } from "../../../slices/lt1/settings/reducer";

import { toast } from "react-toastify";

import "./Dashboard.css";
import { dashboardCache } from "../../../helpers/dashboardCache";
import {
  PHASE_COLORS,
  MULTIPLIER_OPTIONS,
  SITE_TITLE,
  CACHE_KEYS,
  RETRY_INTERVAL,
  SOCKET_EVENTS
} from "../../../common/constants";

const Dashboard = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const [customMultiplier, setCustomMultiplier] = useState("");
  const [showCustomModal, setShowCustomModal] = useState(false);
  // Initialize from cache to persist data across route changes and page refreshes
  // Always fall back to an empty array so UI rendering is safe
  const [lastTenWinners, setLastTenWinners] = useState(() => dashboardCache.get(CACHE_KEYS.LT1) || []);
  const [currentRoundWinner, setCurrentRoundWinner] = useState(null);
  const [hoveredWinner, setHoveredWinner] = useState(null);
  const [onlineCount, setOnlineCount] = useState(0);

  const {
    settings,
    timerDisplay,
    phase,
    roundNumber,
    bidPredictions,
    bidDetails,
    totalAmount,
    isManualWinnerWindowOpen,
    selectedWinner,
    loading,
    globalReturnMultiplier,
    lastTenWinners: reduxHistory
  } = useSelector((state) => state.lt1.settings || {});

  // Sync local state with Redux on load
  useEffect(() => {
    if (reduxHistory && reduxHistory.length > 0) {
      setLastTenWinners(reduxHistory);
    }
  }, [reduxHistory]);

  // Fetch settings on mount
  useEffect(() => {
    dispatch(fetchSettingsData());
  }, [dispatch]);

  // Unified Socket Lifecycle Hook
  useEffect(() => {
    if (location.pathname !== "/lucky-target-1/dashboard") return;

    const requestHistory = () => {
      if (!socket) return;
      console.log("LT1 Dashboard: Emitting history requests");
      socket.emit(SOCKET_EVENTS.LT1.HISTORY);
      socket.emit(SOCKET_EVENTS.LT1.HISTORY_FALLBACK); // Singular fallback
      socket.emit(SOCKET_EVENTS.GLOBAL.HISTORY);
      socket.emit(SOCKET_EVENTS.GLOBAL.HISTORY_SINGULAR);     // Global fallback
      socket.emit("getGameOnlineStats", { gameType: "lt1" });
    };

    const handleTimerUpdate = (data) => {
      dispatch(updateGameState({
        timeLeft: data.timeLeft,
        phase: data.phase,
        roundNumber: data.roundNumber,
        status: data.status
      }));
    };

    const handleBidUpdate = (data) => {
      dispatch(updateBidPredictions({
        bids: data.bids || data.predictions || {},
        totalAmount: data.totalAmount || 0,
        numberTotals: data.numberTotals || data.bidAmounts || {}
      }));
    };

    const handleManualWinnerWindow = (data) => {
      dispatch(setManualWinnerWindow(data.isOpen !== undefined ? data.isOpen : true));
    };

    const handleNewRound = (data) => {
      dispatch(resetRound());
      setCurrentRoundWinner(null);
      if (data && data.lastTenWinners && Array.isArray(data.lastTenWinners)) {
        dashboardCache.set(CACHE_KEYS.LT1, data.lastTenWinners);
        setLastTenWinners(data.lastTenWinners);
        dispatch(updateHistory(data.lastTenWinners)); // Sync Redux
      } else {
        requestHistory();
      }
    };

    const handleLastTenWinners = (data) => {
      console.log("LT1 Socket: History data received", data);
      let winnersArray = null;
      if (data) {
        if (data.data && data.data.lastTenWinners && Array.isArray(data.data.lastTenWinners)) winnersArray = data.data.lastTenWinners;
        else if (data.lastTenWinners && Array.isArray(data.lastTenWinners)) winnersArray = data.lastTenWinners;
        else if (Array.isArray(data)) winnersArray = data;
        else if (data.data && Array.isArray(data.data)) winnersArray = data.data;
      }

      if (winnersArray && winnersArray.length > 0) {
        dashboardCache.set(CACHE_KEYS.LT1, winnersArray);
        setLastTenWinners(winnersArray);
        dispatch(updateHistory(winnersArray)); // Sync Redux
      }
    };

    const handlePlaySpin = (data) => {
      if (data && data.winningNumber !== undefined) {
        setCurrentRoundWinner({
          roundNumber: data.roundNumber,
          winningNumber: data.winningNumber,
          winnerInfo: data.winnerInfo,
          timestamp: data.timestamp
        });
      }
      dispatch(setManualWinnerWindow(false)); // Lock on spin
    };

    const handleRoundCompleted = () => {
      dispatch(setManualWinnerWindow(false));
    };

    const handlePlaceBid = (data) => {
      if (data && data.success && data.data) {
        const { numberTotals, bids, totalAmount: newTotalAmount } = data.data;
        const bidCounts = {};
        if (bids && Array.isArray(bids)) {
          bids.forEach(bid => {
            const num = bid.bidNumber;
            bidCounts[num] = (bidCounts[num] || 0) + 1;
          });
        }
        dispatch(updateBidPredictions({
          bids: bidCounts,
          totalAmount: newTotalAmount,
          numberTotals: numberTotals || {},
          isIncremental: true
        }));
      }
    };

    const handleConnect = () => {
      requestHistory();
    };

    const handleOnlineStats = (data) => {
      if (data && data.gameType === "lt1") {
        setOnlineCount(data.onlineUsersCount);
      }
    };

    // 1. Listeners FIRST
    socket.on(SOCKET_EVENTS.LT1.TIMER, handleTimerUpdate);
    socket.on(SOCKET_EVENTS.LT1.BID_UPDATE, handleBidUpdate);
    socket.on(SOCKET_EVENTS.LT1.MANUAL_WINDOW, handleManualWinnerWindow);
    socket.on(SOCKET_EVENTS.LT1.NEW_ROUND, handleNewRound);
    socket.on(SOCKET_EVENTS.LT1.ROUND_COMPLETED, handleRoundCompleted);
    socket.on(SOCKET_EVENTS.LT1.PLACE_BID, handlePlaceBid);
    socket.on(SOCKET_EVENTS.LT1.HISTORY, handleLastTenWinners);
    socket.on(SOCKET_EVENTS.LT1.HISTORY_FALLBACK, handleLastTenWinners);
    socket.on(SOCKET_EVENTS.GLOBAL.HISTORY, handleLastTenWinners);
    socket.on(SOCKET_EVENTS.GLOBAL.HISTORY_SINGULAR, handleLastTenWinners);
    socket.on(SOCKET_EVENTS.LT1.PLAY_SPIN, handlePlaySpin);
    socket.on("connect", handleConnect);
    socket.on("admin-onlineStatsResponse", handleOnlineStats);

    // 2. Initial Request
    requestHistory();

    // 3. Retry Loop
    const retryTimer = setInterval(() => {
      if (lastTenWinners.length === 0 && socket.connected) {
        requestHistory();
      }
    }, RETRY_INTERVAL);

    return () => {
      clearInterval(retryTimer);
      socket.off(SOCKET_EVENTS.LT1.TIMER, handleTimerUpdate);
      socket.off(SOCKET_EVENTS.LT1.BID_UPDATE, handleBidUpdate);
      socket.off(SOCKET_EVENTS.LT1.MANUAL_WINDOW, handleManualWinnerWindow);
      socket.off(SOCKET_EVENTS.LT1.NEW_ROUND, handleNewRound);
      socket.off(SOCKET_EVENTS.LT1.ROUND_COMPLETED, handleRoundCompleted);
      socket.off(SOCKET_EVENTS.LT1.PLACE_BID, handlePlaceBid);
      socket.off(SOCKET_EVENTS.LT1.HISTORY, handleLastTenWinners);
      socket.off(SOCKET_EVENTS.LT1.HISTORY_FALLBACK, handleLastTenWinners);
      socket.off(SOCKET_EVENTS.GLOBAL.HISTORY, handleLastTenWinners);
      socket.off(SOCKET_EVENTS.GLOBAL.HISTORY_SINGULAR, handleLastTenWinners);
      socket.off(SOCKET_EVENTS.LT1.PLAY_SPIN, handlePlaySpin);
      socket.off("connect", handleConnect);
      socket.off("admin-onlineStatsResponse", handleOnlineStats);
    };
  }, [dispatch, location.pathname, lastTenWinners.length]);

  // Handle jackpot multiplier change
  const handleMultiplierChange = useCallback(async (multiplier) => {
    const result = await dispatch(updateSettingsAction({
      globalReturnMultiplier: multiplier,
      manualWinnerEnabled: true
    }));

    if (result && result.success) {
      toast.success("Settings updated successfully");
    } else if (result && result.error) {
      toast.error(result.error);
    }
  }, [dispatch]);

  // Handle custom multiplier submit
  const handleCustomSubmit = () => {
    const value = parseInt(customMultiplier, 10);
    if (value && value > 0) {
      handleMultiplierChange(value);
      setShowCustomModal(false);
      setCustomMultiplier("");
    }
  };

  // Handle manual winner selection
  const handleManualWinner = useCallback((number) => {
    if (!isManualWinnerWindowOpen) return;
    dispatch(setSelectedWinner(number));
    dispatch(declareManualWinner(number));
  }, [dispatch, isManualWinnerWindowOpen]);

  document.title = `Dashboard | ${SITE_TITLE}`;

  const multiplierOptions = MULTIPLIER_OPTIONS;
  const numbers = [1, 2, 3, 4, 5, 6, 7, 8, 9, 0];

  // Ensure globalReturnMultiplier is a number for comparison
  const currentMultiplier = globalReturnMultiplier ? Number(globalReturnMultiplier) : null;

  // Check if current multiplier is custom (not in predefined options)
  const isCustomMultiplier = currentMultiplier && !multiplierOptions.includes(currentMultiplier);

  // Get phase display color
  const getPhaseColor = () => {
    return PHASE_COLORS[phase] || PHASE_COLORS.default;
  };

  // Format currency
  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return "point 0";
    return `point ${amount.toLocaleString()}`;
  };

  // Calculate total bids count
  const calculateTotalBids = () => {
    return Object.values(bidPredictions).reduce((sum, count) => sum + count, 0);
  };

  // Calculate payout and admin P&L for a selected/hovered number
  const calculatePayoutAndPL = (num) => {
    if (num === null || num === undefined) return null;
    const details = bidDetails[String(num)] || { count: 0, totalAmount: 0 };
    const winningAmount = details.totalAmount || 0;
    const payout = winningAmount * 9; // 9x multiplier for 10-number game
    const totalCollected = Object.values(bidDetails).reduce((sum, detail) => sum + (Number(detail.totalAmount) || 0), 0);
    const adminPL = totalCollected - payout;
    return { payout, adminPL, totalCollected };
  };

  // Get the number to show calculations for (hovered takes priority, then selected)
  const activeManualNumber = hoveredWinner !== null ? hoveredWinner : selectedWinner;
  const manualCalc = calculatePayoutAndPL(activeManualNumber);

  return (
    <React.Fragment>
      <div className="page-content" style={{ marginBottom: '35px' }}>
        <Container fluid>
          {/* Header with Timer */}
          <div className="dashboard-header">
            <div>
              <h4 className="dashboard-title d-flex align-items-center mb-2">
                Dashboard 

              </h4>
              <div className="round-info">
                <span className="round-number">Round #{roundNumber || 0}</span>
                <span className="phase-badge" style={{ backgroundColor: getPhaseColor() }}>
                  {(phase || "waiting").toUpperCase()}
                </span>
                {currentRoundWinner && (
                  <span className="current-winner-badge" style={{
                    backgroundColor: "var(--vz-success)",
                    color: "white",
                    padding: "4px 12px",
                    borderRadius: "4px",
                    marginLeft: "8px",
                    fontSize: "14px",
                    fontWeight: "600"
                  }}>
                    Winner: {currentRoundWinner.winningNumber}
                  </span>
                )}
              </div>
            </div>
            <div className="game-timer">{timerDisplay || "00:00"}</div>
          </div>

          {/* Joker Status Card */}


          {/* Settings and Last 10 Winners Row */}
          <Row>
            <Col md={6}>
              {/* Settings Card */}
              <Card className="settings-card">
                <CardHeader>
                  <h5 className="mb-0">Settings</h5>
                </CardHeader>
                <CardBody>
                  <Row>
                    <Col md={12}>
                      <div className="settings-row">
                        <Label className="settings-label">Add Jackpot:</Label>
                        <div className="multiplier-btn-group">
                          {multiplierOptions.map((mult) => (
                            <Button
                              key={mult}
                              className={`multiplier-btn ${currentMultiplier === mult ? 'active' : ''}`}
                              onClick={() => handleMultiplierChange(mult)}
                            >
                              {mult}x
                            </Button>
                          ))}

                          {/* Show custom multiplier button if it's a custom value */}
                          {isCustomMultiplier && (
                            <Button
                              className="multiplier-btn active"
                              onClick={() => setShowCustomModal(true)}
                            >
                              {currentMultiplier}x
                            </Button>
                          )}

                          {/* Custom button to add new custom value */}
                          <Button
                            className="multiplier-btn custom-btn"
                            onClick={() => setShowCustomModal(true)}
                          >
                            + Custom
                          </Button>
                        </div>
                      </div>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
            <Col md={6}>
              {/* Recent Winning Numbers Card */}
              <Card className="settings-card">
                <CardHeader>
                  <h5 className="mb-0">
                    <i className="ri-trophy-line me-2"></i>
                    Recent Winning Numbers
                  </h5>
                </CardHeader>
                <CardBody>
                  <div className="winners-grid">
                    {lastTenWinners.length > 0 ? (
                      <>
                        {lastTenWinners.map((winner, index) => {
                          const displayWinner = typeof winner === 'object' && winner !== null 
                              ? (winner.winningSymbol !== undefined ? winner.winningSymbol : (winner.winningNumber !== undefined ? winner.winningNumber : ''))
                              : winner;
                          return (
                            <div key={index} className="winner-number-badge">
                              {displayWinner}
                            </div>
                          );
                        })}
                      </>
                    ) : (
                      <div className="text-muted text-center" style={{ padding: '10px 0' }}>
                        <i className="ri-inbox-line me-2"></i>
                        No winning numbers recorded yet
                      </div>
                    )}
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Result Prediction Card */}
          <Card className="prediction-card">
            <CardHeader className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Result Prediction</h5>
              <span className="total-amount">
                Total Point: {Object.values(bidDetails).reduce((sum, detail) => sum + (Number(detail.totalAmount) || 0), 0).toLocaleString()}
              </span>
            </CardHeader>
            <CardBody>
              <div className="prediction-grid">
                {numbers.map((num) => {
                  const details = bidDetails[String(num)] || { count: 0, totalAmount: 0 };
                  const bidCount = details.count || 0;
                  const amount = details.totalAmount || 0;

                  return (
                    <div key={num} className="number-tile">
                      <span className="tile-number">{num}</span>
                      <div className="d-flex justify-content-between mt-1 gap-2">
                        <span className="tile-bid-count">Bids: {bidCount}</span>
                        <span className={`tile-bid-amount ${amount > 0 ? 'has-bids' : ''}`}>{formatCurrency(amount)}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardBody>
          </Card>

          {/* Declare Result Manually Card */}
          <Card className={`manual-winner-card ${!isManualWinnerWindowOpen ? 'disabled-card' : ''}`}>
            <CardHeader className="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <h5 className="mb-0">
                Declare Result Manually
                {!isManualWinnerWindowOpen && (
                  <span className="ms-2 badge bg-light text-dark">
                    <i className="mdi mdi-lock me-1"></i>Locked
                  </span>
                )}
              </h5>
              <div className="d-flex align-items-center gap-3 flex-wrap">
                {manualCalc && activeManualNumber !== null && activeManualNumber !== undefined && (
                  <div className="manual-calc-display">
                    <span className="calc-label">Number: <strong>{activeManualNumber}</strong></span>
                    <span className="calc-divider">|</span>
                    <span className="calc-label">Payout: <strong className="text-warning">{manualCalc.payout.toLocaleString()}</strong></span>
                    <span className="calc-divider">|</span>
                    <span className="calc-label">Admin P&L: <strong className={manualCalc.adminPL >= 0 ? 'text-success' : 'text-danger'}>{manualCalc.adminPL >= 0 ? '+' : ''}{manualCalc.adminPL.toLocaleString()}</strong></span>
                  </div>
                )}
                {isManualWinnerWindowOpen ? (
                  <span className="manual-window-badge">Window Open</span>
                ) : (
                  <span className="manual-window-badge closed">Window Closed</span>
                )}
              </div>
            </CardHeader>
            <CardBody>
              <div className="manual-winner-grid">
                {numbers.map((num) => {
                  const details = bidDetails[String(num)] || { count: 0, totalAmount: 0 };
                  const bidCount = details.count || 0;
                  const amount = details.totalAmount || 0;

                  return (
                    <Button
                      key={num}
                      className={`manual-winner-btn ${selectedWinner === num ? 'selected' : ''} ${hoveredWinner === num ? 'hovered' : ''} ${!isManualWinnerWindowOpen ? 'disabled-btn' : ''}`}
                      onClick={() => handleManualWinner(num)}
                      onMouseEnter={() => setHoveredWinner(num)}
                      onMouseLeave={() => setHoveredWinner(null)}
                      disabled={!isManualWinnerWindowOpen || loading}
                      title={!isManualWinnerWindowOpen ? "Manual winner window is closed" : `Select ${num} as winner`}
                    >
                      <span className="winner-number">{num}</span>
                      <div className="d-flex justify-content-between mt-1 gap-2">
                        <span className="winner-bid-count">Bids: {bidCount}</span>
                        <span className={`winner-amount ${amount > 0 ? 'has-bids' : ''}`}>{formatCurrency(amount)}</span>
                      </div>
                    </Button>
                  );
                })}
              </div>
            </CardBody>
          </Card>
        </Container>
      </div>

      {/* Custom Multiplier Modal */}
      <Modal isOpen={showCustomModal} toggle={() => setShowCustomModal(false)}>
        <ModalHeader toggle={() => setShowCustomModal(false)}>
          Set Custom Multiplier
        </ModalHeader>
        <ModalBody>
          <div className="custom-multiplier-input">
            <Label>Enter Multiplier Value:</Label>
            <Input
              type="number"
              value={customMultiplier}
              onChange={(e) => setCustomMultiplier(e.target.value)}
              placeholder="e.g., 15"
              min="1"
            />
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setShowCustomModal(false)}>
            Cancel
          </Button>
          <Button color="primary" onClick={handleCustomSubmit}>
            Apply
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default Dashboard;